/**
 * FP2-header.js
 * Black & Blanc-inspired Navigation
 * Flower Point | Palm Gardens LLC | Dubai, UAE
 */

(function () {
  'use strict';

  /* ── State ─────────────────────────────── */
  let isDrawerOpen    = false;
  let isSearchOpen    = false;
  let isCityOpen      = false;
  let isLangOpen      = false;
  let scrollRAF       = null;
  let isScrolled      = false;

  /* ── Elements ──────────────────────────── */
  const root          = document.getElementById('fp2-header-root');
  const header        = document.getElementById('fp2-header');
  const hamburger     = document.getElementById('fp2-hamburger');
  const drawer        = document.getElementById('fp2-drawer');
  const drawerClose   = document.getElementById('fp2-drawer-close');
  const overlay       = document.getElementById('fp2-overlay');
  const searchBtn     = document.getElementById('fp2-search-btn');
  const searchOverlay = document.getElementById('fp2-search-overlay');
  const searchClose   = document.getElementById('fp2-search-close');
  const searchInput   = document.getElementById('fp2-search-input');
  const cityBtn       = document.getElementById('fp2-city-btn');
  const cityDropdown  = document.getElementById('fp2-city-dropdown');
  const langBtn       = document.getElementById('fp2-lang-btn');
  const langDropdown  = document.getElementById('fp2-lang-dropdown');
  const drawerToggles = document.querySelectorAll('.fp2-drawer__toggle');

  /* ── Scroll ────────────────────────────── */
  function handleScroll() {
    const y = window.scrollY;
    const shouldScroll = y > 8;
    if (shouldScroll !== isScrolled) {
      isScrolled = shouldScroll;
      root && root.classList.toggle('is-scrolled', isScrolled);
    }
  }

  window.addEventListener('scroll', () => {
    if (scrollRAF) cancelAnimationFrame(scrollRAF);
    scrollRAF = requestAnimationFrame(handleScroll);
  }, { passive: true });

  /* ── Drawer ────────────────────────────── */
  function openDrawer() {
    if (!drawer || !overlay) return;
    isDrawerOpen = true;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-visible');
    overlay.setAttribute('aria-hidden', 'false');
    hamburger && hamburger.setAttribute('aria-expanded', 'true');
    hamburger && hamburger.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    // Focus first link
    setTimeout(() => {
      const first = drawer.querySelector('a, button');
      if (first) first.focus();
    }, 420);
  }

  function closeDrawer() {
    if (!drawer || !overlay) return;
    isDrawerOpen = false;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-visible');
    overlay.setAttribute('aria-hidden', 'true');
    hamburger && hamburger.setAttribute('aria-expanded', 'false');
    hamburger && hamburger.classList.remove('is-active');
    document.body.style.overflow = '';
    hamburger && hamburger.focus();
  }

  hamburger  && hamburger.addEventListener('click',  () => isDrawerOpen ? closeDrawer() : openDrawer());
  drawerClose && drawerClose.addEventListener('click', closeDrawer);
  overlay    && overlay.addEventListener('click',    () => {
    if (isDrawerOpen)  closeDrawer();
    if (isSearchOpen)  closeSearch();
  });

  /* ── Drawer Sub-menus ───────────────────── */
  drawerToggles.forEach((toggle) => {
    toggle.addEventListener('click', () => {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const subId   = toggle.getAttribute('aria-controls');
      const sublist = document.getElementById(subId);

      // Close siblings
      drawerToggles.forEach((t) => {
        if (t === toggle) return;
        t.setAttribute('aria-expanded', 'false');
        const sid = t.getAttribute('aria-controls');
        const sl  = document.getElementById(sid);
        if (sl) { sl.classList.remove('is-open'); sl.setAttribute('aria-hidden', 'true'); }
      });

      toggle.setAttribute('aria-expanded', String(!isExpanded));
      if (sublist) {
        sublist.classList.toggle('is-open', !isExpanded);
        sublist.setAttribute('aria-hidden', String(isExpanded));
      }
    });
  });

  /* ── Search ─────────────────────────────── */
  function openSearch() {
    if (!searchOverlay) return;
    isSearchOpen = true;
    searchOverlay.classList.add('is-open');
    searchOverlay.setAttribute('aria-hidden', 'false');
    setTimeout(() => { if (searchInput) searchInput.focus(); }, 200);
  }

  function closeSearch() {
    if (!searchOverlay) return;
    isSearchOpen = false;
    searchOverlay.classList.remove('is-open');
    searchOverlay.setAttribute('aria-hidden', 'true');
  }

  searchBtn   && searchBtn.addEventListener('click', openSearch);
  searchClose && searchClose.addEventListener('click', closeSearch);

  /* ── City Dropdown ──────────────────────── */
  function openCity() {
    isCityOpen = true;
    cityDropdown && cityDropdown.classList.add('is-open');
    cityDropdown && cityDropdown.setAttribute('aria-hidden', 'false');
    cityBtn && cityBtn.setAttribute('aria-expanded', 'true');
  }

  function closeCity() {
    isCityOpen = false;
    cityDropdown && cityDropdown.classList.remove('is-open');
    cityDropdown && cityDropdown.setAttribute('aria-hidden', 'true');
    cityBtn && cityBtn.setAttribute('aria-expanded', 'false');
  }

  if (cityBtn) {
    cityBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      isCityOpen ? closeCity() : openCity();
      if (isLangOpen) closeLang();
    });
  }

  // City option select
  document.querySelectorAll('.fp2-announce__city-option').forEach((opt) => {
    opt.addEventListener('click', () => {
      document.querySelectorAll('.fp2-announce__city-option').forEach(o => {
        o.classList.remove('is-active');
        o.setAttribute('aria-selected', 'false');
      });
      opt.classList.add('is-active');
      opt.setAttribute('aria-selected', 'true');

      // Update label
      const label = cityBtn && cityBtn.querySelector('.fp2-announce__city-label');
      if (label) label.innerHTML = `Select city: <strong>${opt.dataset.city}</strong>`;
      closeCity();
    });
  });

  /* ── Language Dropdown ──────────────────── */
  function openLang() {
    isLangOpen = true;
    langDropdown && langDropdown.classList.add('is-open');
    langDropdown && langDropdown.setAttribute('aria-hidden', 'false');
    langBtn && langBtn.setAttribute('aria-expanded', 'true');
  }

  function closeLang() {
    isLangOpen = false;
    langDropdown && langDropdown.classList.remove('is-open');
    langDropdown && langDropdown.setAttribute('aria-hidden', 'true');
    langBtn && langBtn.setAttribute('aria-expanded', 'false');
  }

  if (langBtn) {
    langBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      isLangOpen ? closeLang() : openLang();
      if (isCityOpen) closeCity();
    });
  }

  document.querySelectorAll('.fp2-announce__lang-option').forEach((opt) => {
    opt.addEventListener('click', () => {
      document.querySelectorAll('.fp2-announce__lang-option').forEach(o => {
        o.classList.remove('is-active');
        o.setAttribute('aria-selected', 'false');
      });
      opt.classList.add('is-active');
      opt.setAttribute('aria-selected', 'true');

      const label = langBtn && langBtn.querySelector('.fp2-announce__lang-label');
      if (label) label.textContent = opt.textContent.trim();
      closeLang();
    });
  });

  /* ── Global close on outside click ────── */
  document.addEventListener('click', (e) => {
    if (isCityOpen && cityBtn && !cityBtn.contains(e.target) && cityDropdown && !cityDropdown.contains(e.target)) {
      closeCity();
    }
    if (isLangOpen && langBtn && !langBtn.contains(e.target) && langDropdown && !langDropdown.contains(e.target)) {
      closeLang();
    }
  });

  /* ── Keyboard: Escape ───────────────────── */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (isDrawerOpen)  closeDrawer();
    if (isSearchOpen)  closeSearch();
    if (isCityOpen)    closeCity();
    if (isLangOpen)    closeLang();
  });

  /* ── Cart count update ──────────────────── */
  function updateCartBadge(count) {
    const badge = document.getElementById('fp2-cart-badge');
    if (!badge) return;
    badge.textContent = count;
    badge.style.display = count > 0 ? '' : 'none';
  }

  document.addEventListener('cart:updated', (e) => {
    if (e.detail && typeof e.detail.itemCount !== 'undefined') {
      updateCartBadge(e.detail.itemCount);
    }
  });

  /* ── Init scroll check ──────────────────── */
  handleScroll();

})();
